import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Compass, ArrowRight, Sparkles, Search } from "lucide-react";
import { toast } from "sonner";

export default function Discover() {
  const [q, setQ] = useState("");
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [about, setAbout] = useState("");
  const [matches, setMatches] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/discover/roles${q ? `?q=${encodeURIComponent(q)}` : ""}`);
      setRoles(data.items || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const findFit = async () => {
    if (!about.trim()) return;
    setBusy(true); setMatches(null);
    try {
      const { data } = await api.post("/discover/fit", { about_me: about });
      setMatches(data);
    } catch { toast.error("Compass unavailable"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-10" data-testid="discover-page">
      <div>
        <div className="label-caps mb-2 flex items-center gap-1.5"><Compass className="h-3 w-3 text-sky-400" />Pillar · Discover</div>
        <h1 className="text-3xl sm:text-5xl tracking-tight font-semibold">Find a vocation that fits.</h1>
        <p className="text-muted-foreground mt-3 max-w-2xl">
          Browse 60+ grounded executive and professional vocations, or ask the Vocation Compass to recommend based on who you are.
        </p>
      </div>

      {/* Compass */}
      <Card className="p-6 md:p-8">
        <div className="label-caps mb-2 flex items-center gap-1.5"><Sparkles className="h-3 w-3 text-primary" />Vocation Compass</div>
        <h2 className="text-xl md:text-2xl font-medium">Describe yourself. Get 3-5 fitting vocations.</h2>
        <div className="mt-4 grid md:grid-cols-5 gap-4">
          <div className="md:col-span-3">
            <Label className="mb-1.5 block">Your strengths, interests, non-negotiables</Label>
            <Textarea value={about} onChange={(e) => setAbout(e.target.value)} rows={5}
              placeholder="e.g. I love turning raw research into shipped products. I want to lead a technical team but keep hands on code. I can't do politics-heavy orgs."
              data-testid="discover-about" />
            <Button className="mt-4 rounded-full" onClick={findFit} disabled={busy || !about.trim()} data-testid="discover-fit-btn">
              <Sparkles className="h-4 w-4 mr-1" />{busy ? "Charting…" : "Find my vocations"}
            </Button>
          </div>
          <div className="md:col-span-2">
            {matches && matches.matches?.length > 0 ? (
              <div className="space-y-3" data-testid="discover-matches">
                {matches.matches.map((m, i) => (
                  <div key={i} className="border-l-2 border-primary pl-3">
                    <div className="text-sm font-medium">{m.role}</div>
                    <div className="text-xs text-muted-foreground mt-1">{m.why_fit}</div>
                  </div>
                ))}
                {matches.coach_note && (
                  <div className="text-xs text-muted-foreground italic pt-2">{matches.coach_note}</div>
                )}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">
                The compass will search 60+ vocation chapters and recommend the strongest fits.
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Browse */}
      <div>
        <div className="flex items-center justify-between gap-4 mb-5">
          <div className="label-caps">All vocations</div>
          <div className="relative w-full max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Search vocations…" className="pl-9 h-9" data-testid="discover-search" />
          </div>
        </div>
        {loading ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {roles.map((r) => (
              <Link key={r.id} to={`/app/learn/chapter/${r.id}`} data-testid={`discover-role-${r.id}`}>
                <Card className="p-5 h-full hover:border-sky-400/50 transition-colors duration-200">
                  <div className="label-caps text-[9px] mb-1.5">{r.section}</div>
                  <div className="text-base font-medium leading-snug">{r.title}</div>
                  <div className="text-xs text-muted-foreground mt-1">{r.topic}</div>
                  <div className="mt-4 flex items-center text-xs text-sky-400">
                    Open brief <ArrowRight className="h-3 w-3 ml-1" />
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
